package co.maytes.standupbot.dashboard.pages.app.onboarding

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.features.onboarding.ConnectionCard
import co.maytes.standupbot.dashboard.features.onboarding.ConnectionState
import co.maytes.standupbot.dashboard.features.onboarding.FlowNode
import co.maytes.standupbot.dashboard.features.onboarding.InlineError
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsStore
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingShell
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingStep
import co.maytes.standupbot.dashboard.features.onboarding.TickRow
import co.maytes.standupbot.dashboard.features.onboarding.WizardBrand
import co.maytes.standupbot.dashboard.features.onboarding.WizardFlow
import co.maytes.standupbot.dashboard.features.onboarding.WizardList
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.integration.LinearAuthorizeUrlResponseDto
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/app/onboarding/linear")
@Composable
fun OnboardingLinearPage() {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val integrations = remember { IntegrationsStore(api) }
    var refreshed by remember { mutableStateOf(false) }
    var connecting by remember { mutableStateOf(false) }
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        val wsId = workspaceId ?: return@LaunchedEffect
        integrations.refresh(wsId)
        refreshed = true
    }

    val connected = auth.currentUser?.workspace?.linearConnected == true ||
        integrations.isConnected(IntegrationProviderDto.LINEAR)
    val connectionState = when {
        connected -> ConnectionState.Connected
        !refreshed && workspaceId != null -> ConnectionState.Checking
        else -> ConnectionState.Disconnected
    }

    OnboardingShell(
        step = OnboardingStep.Linear,
        headline = if (connected) "Linear is connected" else "Connect Linear",
        subline = if (connected) {
            "Next, pick the channel and the people on the standup."
        } else {
            "Read-only access, scoped to the standups you choose."
        },
        art = {
            WizardFlow(
                nodes = listOf(
                    FlowNode(WizardBrand.Linear, live = connected),
                    FlowNode(WizardBrand.StandupBot, live = connected),
                    FlowNode(WizardBrand.Slack, live = auth.currentUser?.workspace?.slackConnected == true),
                ),
                flowing = !connected,
            )
        },
        nextLabel = if (connected) "Continue" else "Connect Linear",
        nextLoading = connecting,
        onNext = if (connected) {
            { ctx.router.navigateTo(OnboardingStep.Setup.path) }
        } else {
            {
                val wsId = workspaceId
                if (wsId != null) {
                    scope.launch {
                        connecting = true
                        val res = api.get<LinearAuthorizeUrlResponseDto>(
                            "/api/v1/workspaces/$wsId/integrations/linear/authorize-url",
                        )
                        when (res) {
                            is Result.Ok -> window.location.href = res.value.url
                            is Result.Err -> {
                                connecting = false
                                ui.showToast(
                                    "Couldn't start Linear OAuth: ${res.error.displayMessage}",
                                    ToastKind.Error,
                                )
                            }
                        }
                    }
                }
            }
        },
    ) {
        Div(attrs = { classes("sb-wiz-stack") }) {
            ConnectionCard(
                brand = WizardBrand.Linear,
                title = "Linear workspace",
                detail = if (connected) "Read-only access is in place." else "Reads activity for your standups.",
                state = connectionState,
            )
            val loadError = integrations.lastError
            if (refreshed && loadError != null) {
                InlineError(
                    title = "Couldn't load integration status",
                    message = loadError,
                    onRetry = {
                        val wsId = workspaceId ?: return@InlineError
                        scope.launch {
                            refreshed = false
                            integrations.refresh(wsId)
                            refreshed = true
                        }
                    },
                )
            }
        }
        Div(attrs = { classes("sb-wiz-stack") }) {
            Span(attrs = { classes("sb-wiz-label") }) { Text("What we read") }
            WizardList {
                TickRow("Assignee activity")
                TickRow("Issue transitions")
                TickRow("Pull request links")
            }
        }
    }
}
